"use client"

import { useState } from "react"
import { Category, Project, Subcategory, SubcategoryInfo } from "../../../lib/types"
import { Button } from "../../../components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../../components/ui/card"
import { ArrowLeft, Eye, Plus, SaveIcon, Trash2, FolderOpen, FolderPlus, ChevronRight, Pencil, Tag } from "lucide-react"
import { SelectProjectsModal } from "./select-projects-modal"
import { AddSubcategoryModal } from "./add-subcategory-modal"
import { SubcategoryInfoModal, formatSubcategoryPrice } from "./subcategory-info-modal"
import { SubcategoryDetail } from "./subcategory-detail"
import { Badge } from "../../../components/ui/badge"
import { FileText } from "lucide-react" // Import FileText here
import { supbaseManager, getUserId } from "../../../API"
import { UpdateIcon } from "@radix-ui/react-icons"

interface CategoryDetailProps {
    category: Category
    allProjects: Project[]
    onBack: () => void
    onRemoveProject: (categoryId: string, projectId: string) => void
    onAddProjects: (categoryId: string, projectIds: string[]) => void
    onViewProject: (categoryId: string, projectId: string) => void
    onAddSubcategory: (categoryId: string, name: string, thumbnail?: string) => void
    onDeleteSubcategory: (categoryId: string, subcategoryId: string) => void
    onAddProjectsToSubcategory: (categoryId: string, subcategoryId: string, projectIds: string[]) => void
    onRemoveProjectFromSubcategory: (categoryId: string, subcategoryId: string, projectId: string) => void
    onUpdateSubcategoryInfo: (categoryId: string, subcategoryId: string, info: SubcategoryInfo) => void
}

export function CategoryDetail({
    category,
    allProjects,
    onBack,
    onRemoveProject,
    onAddProjects,
    onViewProject,
    onAddSubcategory,
    onDeleteSubcategory,
    onAddProjectsToSubcategory,
    onRemoveProjectFromSubcategory,
    onUpdateSubcategoryInfo,
}: CategoryDetailProps) {
    const [selectModalOpen, setSelectModalOpen] = useState(false)
    const [selectedSubcategory, setSelectedSubcategory] = useState<Subcategory | null>(null)
    const [infoModalSubcategory, setInfoModalSubcategory] = useState<Subcategory | null>(null)

    const subcategories = category.subcategories || []

    // Projetos já organizados dentro de alguma subcategoria não aparecem
    // de novo na lista "solta" da categoria, pra não duplicar visualmente.
    const projectIdsInSubcategories = new Set(
        subcategories.flatMap((sub) => sub.projectIds)
    )

    const categoryProjects = allProjects.filter((project) =>
        category.projectIds.includes(project.id)
    )

    const unfiledProjects = categoryProjects.filter(
        (project) => !projectIdsInSubcategories.has(project.id)
    )

    const handleAddProjects = (projectIds: string[]) => {
        const newProjectIds = projectIds.filter(
            (id) => !category.projectIds.includes(id)
        )
        if (newProjectIds.length > 0) {
            onAddProjects(category.id, newProjectIds)
        }
    }

    const saveCategory = async function () {

        if (window.confirm("Salvar categoria?")) {
            const saved = await supbaseManager(
                "insert",
                "categories_new",
                {
                    user_id: getUserId(),
                    json: JSON.stringify(category),
                    category_id: category.id
                },
            );
        }
    }

    const updateCategory = async function () {

        if (window.confirm("Atualizar categoria?")) {
            const updated = await supbaseManager(
                "update",
                "categories_new",
                JSON.stringify(category),
                category.id
            );
        }
    }

    // Se uma subcategoria estiver selecionada, mostra a tela de detalhe dela
    // (mesmo layout, escopado aos projetos daquela subcategoria).
    if (selectedSubcategory) {
        // Mantém a referência sincronizada caso a subcategoria tenha mudado
        // (ex: projeto adicionado) enquanto estava selecionada.
        const currentSubcategory =
            subcategories.find((s) => s.id === selectedSubcategory.id) || selectedSubcategory

        return (
            <SubcategoryDetail
                category={category}
                subcategory={currentSubcategory}
                allProjects={allProjects}
                isShared={!!category.shared}
                onBack={() => setSelectedSubcategory(null)}
                onRemoveProject={(projectId) =>
                    onRemoveProjectFromSubcategory(category.id, currentSubcategory.id, projectId)
                }
                onAddProjects={(projectIds) =>
                    onAddProjectsToSubcategory(category.id, currentSubcategory.id, projectIds)
                }
                onViewProject={(projectId) => onViewProject(category.id, projectId)}
            />
        )
    }

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <Button variant="ghost" size="icon" onClick={onBack}>
                        <ArrowLeft className="h-5 w-5" />
                        <span className="sr-only">Voltar</span>
                    </Button>
                    <div>
                        <h2 className="text-2xl font-bold tracking-tight">{category.name}</h2>
                        <p className="text-muted-foreground">
                            {categoryProjects.length} projeto(s) vinculado(s)
                        </p>
                    </div>
                </div>
                {!category.shared ?
                    <div className="flex">

                        {category.saved == true ? (
                            <Button className="m-2" onClick={() => updateCategory()}>
                                <UpdateIcon className="mr-2 h-4 w-4"></UpdateIcon>
                                Atualizar Categoria
                            </Button>
                        ) : (
                            <Button className="m-2" onClick={() => saveCategory()}>
                                <SaveIcon className="mr-2 h-4 w-4"></SaveIcon>
                                Salvar Categoria
                            </Button>
                        )}

                        <Button className="m-2" onClick={() => setSelectModalOpen(true)}>
                            <Plus className="mr-2 h-4 w-4" />
                            Adicionar Projetos
                        </Button>
                    </div> : null
                }

            </div>

            {/* Subcategorias: opcional, só aparece a seção se houver alguma
                subcategoria criada ou se o usuário puder criar uma nova. */}
            {subcategories.length > 0 || !category.shared ? (
                <div className="space-y-3">
                    <div className="flex items-center justify-between">
                        <h3 className="text-lg font-semibold tracking-tight">Subcategorias</h3>
                        {!category.shared ? (
                            <AddSubcategoryModal
                                onAddSubcategory={(name, thumbnail) =>
                                    onAddSubcategory(category.id, name, thumbnail)
                                }
                            />
                        ) : null}
                    </div>

                    {subcategories.length > 0 ? (
                        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                            {subcategories.map((sub) => {
                                const subProjectCount = categoryProjects.filter((p) =>
                                    sub.projectIds.includes(p.id)
                                ).length

                                return (
                                    <Card
                                        key={sub.id}
                                        className="group cursor-pointer transition-all hover:shadow-md hover:border-primary/50"
                                        onClick={() => setSelectedSubcategory(sub)}
                                    >
                                        <CardHeader className="pb-3">
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-2">
                                                    <div className="rounded-lg bg-primary/10 h-9 w-9 flex items-center justify-center overflow-hidden">
                                                        {sub.thumbnail ? (
                                                            <img
                                                                src={sub.thumbnail}
                                                                alt={sub.name}
                                                                className="h-full w-full object-cover"
                                                            />
                                                        ) : (
                                                            <FolderOpen className="h-5 w-5 text-primary" />
                                                        )}
                                                    </div>
                                                    <div>
                                                        <CardTitle className="text-base">{sub.name}</CardTitle>
                                                        <CardDescription className="text-xs">
                                                            {subProjectCount} projeto(s)
                                                            {sub.info?.price ? ` · ${formatSubcategoryPrice(sub.info.price, sub.info.currency)}` : ""}
                                                        </CardDescription>
                                                    </div>
                                                </div>
                                                <div className="flex items-center">
                                                    <Button
                                                        title="INFORMAÇÕES"
                                                        variant="ghost"
                                                        size="icon"
                                                        className="h-8 w-8"
                                                        onClick={(e) => {
                                                            e.stopPropagation()
                                                            setInfoModalSubcategory(sub)
                                                        }}
                                                    >
                                                        <Pencil className="h-4 w-4" />
                                                        <span className="sr-only">Editar informações da subcategoria</span>
                                                    </Button>
                                                    {!category.shared ? (
                                                        <Button
                                                            title="APAGAR"
                                                            variant="ghost"
                                                            size="icon"
                                                            className="h-8 w-8 text-destructive"
                                                            onClick={(e) => {
                                                                e.stopPropagation()
                                                                if (window.confirm(`Apagar subcategoria "${sub.name}"? Os projetos continuam na categoria.`)) {
                                                                    onDeleteSubcategory(category.id, sub.id)
                                                                }
                                                            }}
                                                        >
                                                            <Trash2 className="h-4 w-4" />
                                                            <span className="sr-only">Excluir subcategoria</span>
                                                        </Button>
                                                    ) : null}
                                                    <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
                                                </div>
                                            </div>
                                            {(sub.info?.fabric || sub.info?.sleeves || sub.info?.collar) ? (
                                                <div className="flex flex-wrap gap-1.5 pt-2">
                                                    {sub.info?.fabric ? (
                                                        <span className="inline-flex items-center gap-1 rounded-full bg-secondary px-2 py-0.5 text-[11px] text-muted-foreground">
                                                            <Tag className="h-3 w-3" /> {sub.info.fabric}
                                                        </span>
                                                    ) : null}
                                                    {sub.info?.sleeves ? (
                                                        <span className="inline-flex items-center gap-1 rounded-full bg-secondary px-2 py-0.5 text-[11px] text-muted-foreground">
                                                            {sub.info.sleeves}
                                                        </span>
                                                    ) : null}
                                                    {sub.info?.collar ? (
                                                        <span className="inline-flex items-center gap-1 rounded-full bg-secondary px-2 py-0.5 text-[11px] text-muted-foreground">
                                                            {sub.info.collar}
                                                        </span>
                                                    ) : null}
                                                </div>
                                            ) : null}
                                        </CardHeader>
                                    </Card>
                                )
                            })}
                        </div>
                    ) : (
                        <p className="text-sm text-muted-foreground">
                            Nenhuma subcategoria criada. Use subcategorias se quiser agrupar projetos dentro de &quot;{category.name}&quot; (opcional).
                        </p>
                    )}
                </div>
            ) : null}

            {unfiledProjects.length === 0 && subcategories.length > 0 ? null : (
                <div className="space-y-3">
                    {subcategories.length > 0 ? (
                        <h3 className="text-lg font-semibold tracking-tight">Sem subcategoria</h3>
                    ) : null}

                    {unfiledProjects.length === 0 ? (
                        <Card>
                            <CardContent className="flex flex-col items-center justify-center py-12">
                                <FileText className="h-12 w-12 text-muted-foreground mb-4" />
                                <h3 className="text-lg font-medium">Nenhum projeto vinculado</h3>
                                <p className="text-sm text-muted-foreground mt-1 mb-4">
                                    Clique em &quot;Adicionar Projetos&quot; para vincular projetos a esta categoria.
                                </p>
                                {!category.shared ? (
                                    <Button onClick={() => setSelectModalOpen(true)}>
                                        <Plus className="mr-2 h-4 w-4" />
                                        Adicionar Projetos
                                    </Button>
                                ) : null}
                            </CardContent>
                        </Card>
                    ) : (
                        <div className="h-[85vh] overflow-y-auto pr-2">
                            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                                {unfiledProjects.map((project) => (
                                    <Card key={project.id} className="group overflow-hidden">
                                        <div className="relative aspect-video">
                                            <img
                                                src={project.thumbnail || "/placeholder.svg"}
                                                alt={project.name}
                                                className="object-cover transition-transform group-hover:scale-105"
                                            />
                                            <div className="flex">
                                                <Button
                                                    variant="secondary"
                                                    size="icon"
                                                    className="absolute top-2 right-12 h-8 w-8 transition-opacity shadow-md"
                                                    onClick={() => onViewProject(category.id, project.id)}
                                                >
                                                    <Eye className="h-4 w-4" />
                                                    <span className="sr-only">Abrir projeto</span>
                                                </Button>
                                                <Button
                                                    variant="secondary"
                                                    size="icon"
                                                    className="absolute top-2 right-2 h-8 w-8 transition-opacity shadow-md"
                                                    onClick={() => onRemoveProject(category.id, project.id)}
                                                >
                                                    <Trash2 className="h-4 w-4 text-destructive" />
                                                    <span className="sr-only">Remover projeto</span>
                                                </Button>
                                            </div>
                                        </div>
                                        <CardHeader className="pb-2">
                                            <CardTitle className="text-base line-clamp-1">{project.name}</CardTitle>
                                        </CardHeader>
                                        <CardContent className="pt-0">
                                            <CardDescription className="text-sm line-clamp-2">
                                                {project.description}
                                            </CardDescription>
                                        </CardContent>
                                    </Card>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}

            <SelectProjectsModal
                open={selectModalOpen}
                onOpenChange={setSelectModalOpen}
                allProjects={allProjects}
                selectedProjectIds={category.projectIds}
                onConfirm={handleAddProjects}
            />

            {infoModalSubcategory ? (
                <SubcategoryInfoModal
                    open={!!infoModalSubcategory}
                    onOpenChange={(open) => { if (!open) setInfoModalSubcategory(null) }}
                    subcategoryName={infoModalSubcategory.name}
                    initialInfo={infoModalSubcategory.info}
                    onSave={(info) => onUpdateSubcategoryInfo(category.id, infoModalSubcategory.id, info)}
                />
            ) : null}
        </div>
    )
}